import { useState, useEffect, createContext, useContext, useCallback, useMemo, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useSearchParams, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, Bed, Bath, Square, Heart, ChevronRight, Menu, X, Home, Building2, Users, MessageSquare, Settings, LogOut, Plus, Send, Bot, Filter, Grid, Map as MapIcon, DollarSign, Phone, Mail, Calendar, Star, ArrowLeft, Check, Loader2, Upload, Image, Download, FileImage, FileText, Eye, Trash2, Shield, Bell, UserCog, Bookmark, BellRing, Share2, Smartphone, Clock, Armchair } from "lucide-react";
import { GoogleMap, LoadScript, Marker, InfoWindow, Autocomplete } from "@react-google-maps/api";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAPS_LIBRARIES = ["places"];

// Configure axios
axios.defaults.withCredentials = true;

// ============ AUTH CONTEXT ============
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, role, phone = null, sms_notifications_enabled = false) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, role, phone, sms_notifications_enabled });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============ PROTECTED ROUTE ============
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/app/login" replace />;
  }
  
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/app" replace />;
  }
  
  return children;
};

// ============ COMING SOON PAGE ============
const ComingSoon = () => {
  return (
    <div className="coming-soon-container" data-testid="coming-soon-page">
      <div className="coming-soon-content">
        <div className="logo-section">
          <div className="logo-circle">
            <span className="logo-text">M</span>
          </div>
        </div>
        <h1 className="coming-soon-title">Coming Soon</h1>
        <p className="coming-soon-subtitle">
          We're building something extraordinary. Stay tuned for our marketplace launch.
        </p>
        <div className="coming-soon-divider"></div>
        <p className="coming-soon-footer">Get notified when we launch</p>
        <div className="email-signup">
          <input type="email" placeholder="Enter your email" className="email-input" data-testid="email-input" />
          <button className="notify-btn" data-testid="notify-btn">Notify Me</button>
        </div>
      </div>
    </div>
  );
};

// ============ NAVBAR ============
const Navbar = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/app");
  };

  return (
    <nav className="navbar" data-testid="navbar">
      <div className="navbar-container">
        <Link to="/app" className="navbar-logo" data-testid="navbar-logo">
          <div className="logo-icon">M</div>
          <span>Marketplace</span>
        </Link>

        <div className="navbar-links">
          <Link to="/app" className="nav-link" data-testid="nav-home">Home</Link>
          <Link to="/app/properties" className="nav-link" data-testid="nav-properties">Properties</Link>
          {user && (
            <Link to="/app/dashboard" className="nav-link" data-testid="nav-dashboard">Dashboard</Link>
          )}
        </div>

        <div className="navbar-actions">
          {user ? (
            <div className="user-menu">
              <span className="user-name">{user.name}</span>
              <Link to="/app/settings" className="btn-icon" data-testid="nav-settings" title="Settings">
                <Settings className="w-4 h-4" />
              </Link>
              <button onClick={handleLogout} className="btn-secondary" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <Link to="/app/login" className="btn-secondary" data-testid="login-btn">Log In</Link>
              <Link to="/app/register" className="btn-primary" data-testid="register-btn">Sign Up</Link>
            </>
          )}
        </div>

        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
          {mobileMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <Link to="/app" onClick={() => setMobileMenuOpen(false)}>Home</Link>
          <Link to="/app/properties" onClick={() => setMobileMenuOpen(false)}>Properties</Link>
          {user ? (
            <>
              <Link to="/app/dashboard" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
              <Link to="/app/settings" onClick={() => setMobileMenuOpen(false)}>Settings</Link>
              <button onClick={handleLogout}>Log Out</button>
            </>
          ) : (
            <>
              <Link to="/app/login" onClick={() => setMobileMenuOpen(false)}>Log In</Link>
              <Link to="/app/register" onClick={() => setMobileMenuOpen(false)}>Sign Up</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

// ============ PROPERTY CARD ============
const PropertyCard = ({ property }) => {
  const [saved, setSaved] = useState(false);

  return (
    <Link to={`/app/properties/${property.id}`} className={`property-card ${property.featured ? "featured-card" : ""}`} data-testid={`property-card-${property.id}`}>
      <div className="property-image">
        <img src={property.images?.[0] || "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?w=800"} alt={property.title} />
        <button 
          className={`save-btn ${saved ? "saved" : ""}`} 
          onClick={(e) => { e.preventDefault(); setSaved(!saved); }}
          data-testid={`save-btn-${property.id}`}
        >
          <Heart className={saved ? "fill-current" : ""} />
        </button>
        <span className="property-type-tag">{property.listing_type === "rent" ? "For Rent" : "For Sale"}</span>
        {property.featured && <span className="featured-badge" data-testid={`featured-badge-${property.id}`}>Featured</span>}
      </div>
      <div className="property-info">
        <div className="property-price">${property.price?.toLocaleString()}{property.listing_type === "rent" ? "/mo" : ""}</div>
        <h3 className="property-title">{property.title}</h3>
        <p className="property-location">
          <MapPin className="w-4 h-4" />
          {property.city}, {property.state}
        </p>
        <div className="property-features">
          <span><Bed className="w-4 h-4" /> {property.bedrooms} Bed</span>
          <span><Bath className="w-4 h-4" /> {property.bathrooms} Bath</span>
          <span><Square className="w-4 h-4" /> {property.area_sqft?.toLocaleString()} sqft</span>
        </div>
        <div className="property-tags">
          {property.lease_terms?.includes("short_term") && (
            <span className="property-tag tag-short-term" data-testid={`tag-short-term-${property.id}`}><Clock className="w-3 h-3" /> ST</span>
          )}
          {property.furnishing === "furnished" && (
            <span className="property-tag tag-furnished" data-testid={`tag-furnished-${property.id}`}><Armchair className="w-3 h-3" /> Furnished</span>
          )}
          {property.furnishing === "partially_furnished" && (
            <span className="property-tag tag-partial" data-testid={`tag-partial-${property.id}`}><Armchair className="w-3 h-3" /> Partial</span>
          )}
        </div>
      </div>
    </Link>
  );
};

// ============ HOME PAGE ============
const HomePage = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLat, setSearchLat] = useState(null);
  const [searchLng, setSearchLng] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [heroFilters, setHeroFilters] = useState({ bedrooms: "", property_type: "", bathrooms: "" });
  const [autocompleteRef, setAutocompleteRef] = useState(null);
  const navigate = useNavigate();

  const gtaBounds = useMemo(() => ({
    north: 44.05, south: 43.40, east: -79.00, west: -80.00
  }), []);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const { data } = await axios.get(`${API}/properties/featured`);
        setFeaturedProperties(data);
      } catch (e) {
        console.error("Error fetching featured:", e);
      }
    };
    fetchFeatured();
  }, []);

  const onAutoLoad = useCallback((ac) => setAutocompleteRef(ac), []);

  const onPlaceChanged = useCallback(() => {
    if (!autocompleteRef) return;
    const place = autocompleteRef.getPlace();
    if (!place || !place.address_components) return;

    let neighborhood = "";
    let city = "";
    let province = "";

    for (const comp of place.address_components) {
      if (comp.types.includes("neighborhood") || comp.types.includes("sublocality_level_1") || comp.types.includes("sublocality")) {
        neighborhood = comp.long_name;
      }
      if (comp.types.includes("locality")) {
        city = comp.long_name;
      }
      if (comp.types.includes("administrative_area_level_1")) {
        province = comp.short_name;
      }
    }

    const searchTerm = neighborhood || city || place.name;
    setSearchQuery(neighborhood ? `${neighborhood}, ${city}, ${province}` : city ? `${city}, ${province}` : place.formatted_address || place.name);

    if (place.geometry?.location) {
      setSearchLat(place.geometry.location.lat());
      setSearchLng(place.geometry.location.lng());
    }
  }, [autocompleteRef]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set("city", searchQuery);
    if (searchLat) params.set("lat", searchLat);
    if (searchLng) params.set("lng", searchLng);
    if (heroFilters.bedrooms) params.set("bedrooms", heroFilters.bedrooms);
    if (heroFilters.property_type) params.set("property_type", heroFilters.property_type);
    if (heroFilters.bathrooms) params.set("bathrooms", heroFilters.bathrooms);
    navigate(`/app/properties?${params.toString()}`);
  };

  return (
    <div className="home-page" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Find Your Perfect Place</h1>
          <p className="hero-subtitle">Discover rental properties and homes that match your lifestyle</p>
          
          <form className="hero-search" onSubmit={handleSearch} data-testid="hero-search-form">
            <div className="search-input-wrapper">
              <Search className="search-icon" />
              <Autocomplete
                onLoad={onAutoLoad}
                onPlaceChanged={onPlaceChanged}
                options={{
                  bounds: gtaBounds,
                  strictBounds: false,
                  types: ["geocode"],
                  componentRestrictions: { country: "ca" }
                }}
              >
                <input
                  type="text"
                  placeholder="Search by city, neighborhood, or address..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setSearchLat(null); setSearchLng(null); }}
                  className="search-input"
                  data-testid="hero-search-input"
                />
              </Autocomplete>
            </div>
            <button type="submit" className="search-btn" data-testid="hero-search-btn">
              Search
            </button>
          </form>

          <div className="hero-advanced-toggle">
            <button type="button" className="advanced-toggle-btn" onClick={() => setShowAdvanced(!showAdvanced)} data-testid="hero-advanced-toggle">
              <Filter className="w-4 h-4" /> {showAdvanced ? "Hide Filters" : "Add Filters"} <span className="optional-label">(optional)</span>
            </button>
          </div>

          {showAdvanced && (
            <div className="hero-advanced-filters" data-testid="hero-advanced-filters">
              <div className="hero-filter-group">
                <label>Bedrooms</label>
                <select value={heroFilters.bedrooms} onChange={(e) => setHeroFilters({ ...heroFilters, bedrooms: e.target.value })} data-testid="hero-filter-bedrooms">
                  <option value="">Any</option>
                  <option value="0">Studio</option>
                  <option value="1">1+</option>
                  <option value="2">2+</option>
                  <option value="3">3+</option>
                  <option value="4">4+</option>
                </select>
              </div>
              <div className="hero-filter-group">
                <label>Property Type</label>
                <select value={heroFilters.property_type} onChange={(e) => setHeroFilters({ ...heroFilters, property_type: e.target.value })} data-testid="hero-filter-property-type">
                  <option value="">Any</option>
                  <option value="apartment">Apartment</option>
                  <option value="house">House</option>
                  <option value="condo">Condo</option>
                  <option value="basement">Basement</option>
                  <option value="townhouse">Townhouse</option>
                </select>
              </div>
              <div className="hero-filter-group">
                <label>Bathrooms</label>
                <select value={heroFilters.bathrooms} onChange={(e) => setHeroFilters({ ...heroFilters, bathrooms: e.target.value })} data-testid="hero-filter-bathrooms">
                  <option value="">Any</option>
                  <option value="1">1+</option>
                  <option value="2">2+</option>
                  <option value="3">3+</option>
                </select>
              </div>
            </div>
          )}

          <div className="hero-stats">
            <div className="stat"><span className="stat-value">500+</span><span className="stat-label">Properties</span></div>
            <div className="stat"><span className="stat-value">200+</span><span className="stat-label">Cities</span></div>
            <div className="stat"><span className="stat-value">1000+</span><span className="stat-label">Happy Tenants</span></div>
          </div>
        </div>
        <div className="hero-image">
          <img src="https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?w=1200" alt="Luxury property" />
        </div>
      </section>

      {/* Featured Properties */}
      <section className="featured-section">
        <div className="section-header">
          <h2>Featured Properties</h2>
          <Link to="/app/properties" className="view-all-link" data-testid="view-all-link">
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="properties-grid" data-testid="featured-properties-grid">
          {featuredProperties.map((property) => (
            <PropertyCard key={property.id} property={property} />
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <h2>How It Works</h2>
        <div className="steps-grid">
          <div className="step">
            <div className="step-icon"><Search /></div>
            <h3>Search</h3>
            <p>Browse thousands of properties with our advanced search filters</p>
          </div>
          <div className="step">
            <div className="step-icon"><Calendar /></div>
            <h3>Book Viewing</h3>
            <p>Schedule property viewings at your convenience</p>
          </div>
          <div className="step">
            <div className="step-icon"><Check /></div>
            <h3>Apply</h3>
            <p>Submit your application and secure your new home</p>
          </div>
        </div>
      </section>

      {/* AI Assistant CTA */}
      <section className="ai-cta">
        <div className="ai-cta-content">
          <Bot className="ai-icon" />
          <h2>Need Help Finding the Perfect Property?</h2>
          <p>Our AI assistant can help you discover properties that match your preferences</p>
          <Link to="/app/properties" className="btn-primary" data-testid="ai-cta-btn">
            <MessageSquare className="w-5 h-5" /> Start Chatting
          </Link>
        </div>
      </section>
    </div>
  );
};

// ============ PROPERTIES LIST PAGE ============
const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ listing_type: "", property_type: "", city: "", bedrooms: "", bathrooms: "", min_price: "", max_price: "", lease_term: "", furnishing: "" });
  const [searchLat, setSearchLat] = useState(null);
  const [searchLng, setSearchLng] = useState(null);
  const [nearbyFallback, setNearbyFallback] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [searchParams] = useSearchParams();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [autocompleteRef, setAutocompleteRef] = useState(null);
  const navigate = useNavigate();

  const mapContainerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const defaultCenter = useMemo(() => ({ lat: 43.6532, lng: -79.3832 }), []); // Toronto

  // Price presets for GTA market
  const pricePresets = useMemo(() => [
    { label: "Any", min: "", max: "" },
    { label: "Under $1,500", min: "", max: "1500" },
    { label: "$1,500 - $2,500", min: "1500", max: "2500" },
    { label: "$2,500 - $4,000", min: "2500", max: "4000" },
    { label: "$4,000 - $6,000", min: "4000", max: "6000" },
    { label: "$6,000+", min: "6000", max: "" },
  ], []);

  // GTA bounds for Places Autocomplete
  const gtaBounds = useMemo(() => ({
    north: 44.05, south: 43.40, east: -79.00, west: -80.00
  }), []);

  const onAutocompleteLoad = useCallback((autocomplete) => {
    setAutocompleteRef(autocomplete);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef) {
      const place = autocompleteRef.getPlace();
      if (place && place.formatted_address) {
        const neighborhood = place.name || place.formatted_address.split(",")[0];
        setFilters(prev => ({ ...prev, city: neighborhood }));
      }
    }
  }, [autocompleteRef]);

  useEffect(() => {
    const city = searchParams.get("city");
    const bedrooms = searchParams.get("bedrooms");
    const property_type = searchParams.get("property_type");
    const bathrooms = searchParams.get("bathrooms");
    const lat = searchParams.get("lat");
    const lng = searchParams.get("lng");
    
    const newFilters = {};
    if (city) newFilters.city = city;
    if (bedrooms) newFilters.bedrooms = bedrooms;
    if (property_type) newFilters.property_type = property_type;
    if (bathrooms) newFilters.bathrooms = bathrooms;
    if (lat) setSearchLat(parseFloat(lat));
    if (lng) setSearchLng(parseFloat(lng));
    
    if (Object.keys(newFilters).length > 0) {
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      setNearbyFallback(false);
      try {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
        if (searchLat && searchLng) {
          params.append("lat", searchLat);
          params.append("lng", searchLng);
          params.append("radius", "10");
        }
        const { data } = await axios.get(`${API}/properties?${params.toString()}`);
        setProperties(data.properties || []);
        if (data.nearby_fallback) setNearbyFallback(true);
      } catch (e) {
        console.error("Error fetching properties:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [filters, searchLat, searchLng]);

  const mapCenter = useMemo(() => {
    if (properties.length > 0) {
      const propsWithCoords = properties.filter(p => p.latitude && p.longitude);
      if (propsWithCoords.length > 0) {
        return { lat: propsWithCoords[0].latitude, lng: propsWithCoords[0].longitude };
      }
    }
    return defaultCenter;
  }, [properties, defaultCenter]);

  const handlePricePreset = (preset) => {
    setFilters(prev => ({ ...prev, min_price: preset.min, max_price: preset.max }));
  };

  // Rental categories for sidebar
  const rentalCategories = useMemo(() => [
    { key: "", label: "All Rentals" },
    { key: "apartment", label: "Long Term Rentals" },
    { key: "short_term", label: "Short Term Rentals" },
    { key: "room", label: "Room Rentals" },
  ], []);

  // Active filter chip dropdown state
  const [openChip, setOpenChip] = useState(null);

  const activeCategory = filters.lease_term === "short_term" ? "short_term" 
    : filters.property_type === "room" ? "room"
    : filters.property_type ? "apartment" : "";

  const handleCategoryClick = (key) => {
    if (key === "short_term") {
      setFilters(prev => ({ ...prev, lease_term: "short_term", property_type: "" }));
    } else if (key === "room") {
      setFilters(prev => ({ ...prev, property_type: "room", lease_term: "" }));
    } else if (key === "apartment") {
      setFilters(prev => ({ ...prev, lease_term: "long_term", property_type: "" }));
    } else {
      setFilters(prev => ({ ...prev, property_type: "", lease_term: "" }));
    }
  };

  const getCategoryLabel = () => {
    if (filters.lease_term === "short_term") return "Short Term Rentals";
    if (filters.property_type) return filters.property_type.charAt(0).toUpperCase() + filters.property_type.slice(1);
    return "All Rentals";
  };

  const getBedroomLabel = () => {
    if (!filters.bedrooms) return "Bedrooms";
    if (filters.bedrooms === "0") return "Studio";
    return `${filters.bedrooms}+ Bed`;
  };

  const getPriceLabel = () => {
    if (filters.min_price || filters.max_price) {
      return `$${filters.min_price || "0"} - $${filters.max_price || "Any"}`;
    }
    return "Price";
  };

  return (
    <div className="properties-page kijiji-layout" data-testid="properties-page">
      {/* Top Filter Chips Bar */}
      <div className="filter-chips-bar" data-testid="filter-chips-bar">
        <div className="filter-chip-wrap">
          <button className={`filter-chip ${activeCategory ? "active" : ""}`} onClick={() => setOpenChip(openChip === "category" ? null : "category")} data-testid="chip-category">
            {getCategoryLabel()} <ChevronRight className={`w-4 h-4 chip-arrow ${openChip === "category" ? "open" : ""}`} />
          </button>
          {openChip === "category" && (
            <div className="chip-dropdown" data-testid="chip-dropdown-category">
              <select value={filters.property_type} onChange={(e) => { setFilters({ ...filters, property_type: e.target.value }); setOpenChip(null); }} data-testid="filter-property-type">
                <option value="">All</option>
                <option value="apartment">Apartment</option>
                <option value="house">House</option>
                <option value="condo">Condo</option>
                <option value="basement">Basement</option>
                <option value="townhouse">Townhouse</option>
              </select>
            </div>
          )}
        </div>
        <div className="filter-chip-wrap">
          <button className={`filter-chip ${filters.min_price || filters.max_price ? "active" : ""}`} onClick={() => setOpenChip(openChip === "price" ? null : "price")} data-testid="chip-price">
            {getPriceLabel()} <ChevronRight className={`w-4 h-4 chip-arrow ${openChip === "price" ? "open" : ""}`} />
          </button>
          {openChip === "price" && (
            <div className="chip-dropdown chip-dropdown-price" data-testid="chip-dropdown-price">
              <div className="chip-price-inputs">
                <input type="number" placeholder="Min" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} data-testid="filter-min-price" />
                <span>to</span>
                <input type="number" placeholder="Max" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} data-testid="filter-max-price" />
              </div>
              <div className="chip-price-presets">
                {pricePresets.slice(1).map(p => (
                  <button key={p.label} className="price-chip-btn" onClick={() => { handlePricePreset(p); setOpenChip(null); }}>{p.label}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="filter-chip-wrap">
          <button className={`filter-chip ${filters.bedrooms ? "active" : ""}`} onClick={() => setOpenChip(openChip === "beds" ? null : "beds")} data-testid="chip-bedrooms">
            {getBedroomLabel()} <ChevronRight className={`w-4 h-4 chip-arrow ${openChip === "beds" ? "open" : ""}`} />
          </button>
          {openChip === "beds" && (
            <div className="chip-dropdown" data-testid="chip-dropdown-beds">
              {[{v:"",l:"Any"},{v:"0",l:"Studio"},{v:"1",l:"1+"},{v:"2",l:"2+"},{v:"3",l:"3+"},{v:"4",l:"4+"}].map(o => (
                <button key={o.v} className={`chip-dd-option ${filters.bedrooms === o.v ? "selected" : ""}`} onClick={() => { setFilters({...filters, bedrooms: o.v}); setOpenChip(null); }}>{o.l}</button>
              ))}
            </div>
          )}
        </div>
        <button className={`filter-chip all-filters-chip ${showFilters ? "active" : ""}`} onClick={() => { setShowFilters(!showFilters); setOpenChip(null); }} data-testid="filter-toggle-btn">
          <Filter className="w-4 h-4" /> All Filters
        </button>
        <div className="filter-chips-right">
          <button className={`view-btn ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")} data-testid="grid-view-btn"><Grid className="w-5 h-5" /></button>
          <button className={`view-btn ${viewMode === "map" ? "active" : ""}`} onClick={() => setViewMode("map")} data-testid="map-view-btn"><MapIcon className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Expanded All Filters Panel */}
      {showFilters && (
        <div className="all-filters-panel" data-testid="filters-panel">
          <div className="filter-group filter-neighborhood">
            <label>Location</label>
            <Autocomplete
              onLoad={onAutocompleteLoad}
              onPlaceChanged={onPlaceChanged}
              options={{
                bounds: gtaBounds,
                strictBounds: true,
                types: ["(regions)"],
                componentRestrictions: { country: "ca" }
              }}
            >
              <input type="text" placeholder="Search GTA neighborhoods..." value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} data-testid="filter-city" />
            </Autocomplete>
          </div>
          <div className="filter-group">
            <label>Listing Type</label>
            <select value={filters.listing_type} onChange={(e) => setFilters({ ...filters, listing_type: e.target.value })} data-testid="filter-listing-type">
              <option value="">All</option>
              <option value="rent">For Rent</option>
              <option value="sale">For Sale</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Bathrooms</label>
            <select value={filters.bathrooms} onChange={(e) => setFilters({ ...filters, bathrooms: e.target.value })} data-testid="filter-bathrooms">
              <option value="">Any</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Lease Term</label>
            <select value={filters.lease_term} onChange={(e) => setFilters({ ...filters, lease_term: e.target.value })} data-testid="filter-lease-term">
              <option value="">Any</option>
              <option value="long_term">Long Term (1 Year+)</option>
              <option value="short_term">Short Term (&lt; 1 Year)</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Furnishing</label>
            <select value={filters.furnishing} onChange={(e) => setFilters({ ...filters, furnishing: e.target.value })} data-testid="filter-furnishing">
              <option value="">Any</option>
              <option value="furnished">Furnished</option>
              <option value="unfurnished">Unfurnished</option>
              <option value="partially_furnished">Partially Furnished</option>
            </select>
          </div>
          <button className="clear-filters-btn" onClick={() => { setFilters({ listing_type: "", property_type: "", city: "", bedrooms: "", bathrooms: "", min_price: "", max_price: "", lease_term: "", furnishing: "" }); setSearchLat(null); setSearchLng(null); }} data-testid="clear-filters-btn">
            <X className="w-4 h-4" /> Clear All
          </button>
        </div>
      )}

      {/* Main Content: Sidebar + Results */}
      <div className="kijiji-main">
        {/* Left Sidebar - Categories */}
        <aside className="kijiji-sidebar" data-testid="category-sidebar">
          <div className="sidebar-section">
            <h3 className="sidebar-heading">Category</h3>
            <ul className="category-list">
              {rentalCategories.map(cat => (
                <li key={cat.key}>
                  <button className={`category-item ${activeCategory === cat.key ? "active" : ""}`} onClick={() => handleCategoryClick(cat.key)} data-testid={`cat-${cat.key || "all"}`}>
                    {cat.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="sidebar-section">
            <h3 className="sidebar-heading">Location</h3>
            <Autocomplete
              onLoad={onAutocompleteLoad}
              onPlaceChanged={onPlaceChanged}
              options={{ bounds: gtaBounds, strictBounds: true, types: ["(regions)"], componentRestrictions: { country: "ca" } }}
            >
              <input type="text" placeholder="City or neighborhood..." value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className="sidebar-input" data-testid="sidebar-location" />
            </Autocomplete>
          </div>
          <div className="sidebar-section">
            <h3 className="sidebar-heading">Price</h3>
            <div className="sidebar-price-inputs">
              <input type="number" placeholder="from" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} data-testid="sidebar-min-price" />
              <input type="number" placeholder="to" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} data-testid="sidebar-max-price" />
            </div>
          </div>
        </aside>

        {/* Results Area */}
        <div className="kijiji-results">
          {nearbyFallback && !loading && (
            <div className="nearby-fallback-notice" data-testid="nearby-fallback-notice">
              <MapPin className="w-5 h-5" />
              <div>
                <strong>No exact match found for your search</strong>
                <span>Showing nearby listings in the area instead</span>
              </div>
            </div>
          )}

          <div className="results-header">
            <span className="results-count" data-testid="results-count">Results {properties.length > 0 ? `1-${properties.length}` : "0"} of {properties.length}</span>
          </div>

          {loading ? (
            <div className="loading-state"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : viewMode === "map" ? (
            <div className="map-split-view" data-testid="map-view">
              <div className="map-sidebar">
                <div className="map-sidebar-header">
                  <span className="results-count">{properties.length} properties</span>
                </div>
                {properties.length === 0 ? (
                  <div className="empty-state"><Home className="w-12 h-12" /><h3>No properties found</h3><p>Try adjusting your filters or search area</p></div>
                ) : (
                  properties.map((property) => (
                    <div key={property.id} className={`map-property-card ${selectedProperty?.id === property.id ? "selected" : ""}`} onClick={() => setSelectedProperty(property)} data-testid={`map-card-${property.id}`}>
                      <img src={property.images?.[0] || "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?w=400"} alt={property.title} />
                      <div className="map-card-info">
                        <div className="map-card-price">${property.price?.toLocaleString()}/mo</div>
                        <div className="map-card-title">{property.title}</div>
                        <div className="map-card-details"><span><Bed className="w-3 h-3" /> {property.bedrooms}</span><span><Bath className="w-3 h-3" /> {property.bathrooms}</span></div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="map-container">
                <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={12}>
                  {properties.filter(p => p.latitude && p.longitude).map((property) => (
                    <Marker key={property.id} position={{ lat: property.latitude, lng: property.longitude }} onClick={() => setSelectedProperty(property)}
                      icon={{ url: property.featured ? "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='18' fill='%23FFB700' stroke='white' stroke-width='3'/%3E%3Ctext x='20' y='26' text-anchor='middle' fill='%230A2540' font-size='14' font-weight='bold'%3E%E2%98%85%3C/text%3E%3C/svg%3E" : "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Ccircle cx='16' cy='16' r='14' fill='%230A2540' stroke='white' stroke-width='2'/%3E%3Ctext x='16' y='21' text-anchor='middle' fill='white' font-size='12' font-weight='bold'%3E$%3C/text%3E%3C/svg%3E", scaledSize: property.featured ? { width: 40, height: 40 } : { width: 32, height: 32 } }} />
                  ))}
                  {selectedProperty && selectedProperty.latitude && selectedProperty.longitude && (
                    <InfoWindow position={{ lat: selectedProperty.latitude, lng: selectedProperty.longitude }} onCloseClick={() => setSelectedProperty(null)}>
                      <div className="map-info-window" onClick={() => navigate(`/app/properties/${selectedProperty.id}`)}>
                        <img src={selectedProperty.images?.[0] || "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?w=200"} alt={selectedProperty.title} />
                        <h4>${selectedProperty.price?.toLocaleString()}/mo</h4>
                        <p>{selectedProperty.title}</p>
                        <span>{selectedProperty.bedrooms} bed · {selectedProperty.bathrooms} bath</span>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              </div>
            </div>
          ) : properties.length === 0 ? (
            <div className="empty-state"><Home className="w-12 h-12" /><h3>No properties found</h3><p>Try adjusting your filters or search area</p></div>
          ) : (
            <div className="kijiji-listings" data-testid="properties-list">
              {properties.map((property) => (
                <div key={property.id} className="kijiji-listing-card" onClick={() => navigate(`/app/properties/${property.id}`)} data-testid={`listing-card-${property.id}`}>
                  <div className="listing-card-image">
                    <img src={property.images?.[0] || "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?w=400"} alt={property.title} />
                    {property.featured && <span className="featured-badge">FEATURED</span>}
                  </div>
                  <div className="listing-card-body">
                    <div className="listing-card-price">${property.price?.toLocaleString()}{property.listing_type === "rent" ? "/mo" : ""}</div>
                    <h3 className="listing-card-title">{property.title}</h3>
                    <div className="listing-card-location"><MapPin className="w-3.5 h-3.5" /> {property.city}, {property.state} {property.distance_km != null && property.distance_km < 9999 ? `· ${property.distance_km}km away` : ""}</div>
                    <p className="listing-card-desc">{property.description?.slice(0, 140)}{property.description?.length > 140 ? "..." : ""}</p>
                    <div className="listing-card-features">
                      <span><Bed className="w-4 h-4" /> {property.bedrooms}</span>
                      <span><Bath className="w-4 h-4" /> {property.bathrooms}</span>
                      <span><Square className="w-4 h-4" /> {property.area_sqft?.toLocaleString()} sqft</span>
                      <span className="capitalize"><Building2 className="w-4 h-4" /> {property.property_type}</span>
                    </div>
                    <div className="listing-card-tags">
                      {property.lease_terms?.includes("short_term") && <span className="property-tag tag-short-term"><Clock className="w-3 h-3" /> ST</span>}
                      {property.furnishing === "furnished" && <span className="property-tag tag-furnished"><Armchair className="w-3 h-3" /> Furnished</span>}
                      {property.furnishing === "partially_furnished" && <span className="property-tag tag-partial"><Armchair className="w-3 h-3" /> Partial</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ IMAGE GALLERY CAROUSEL ============
const ImageGallery = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isLoaded, setIsLoaded] = useState({});

  const allImages = images?.length > 0 ? images : ["https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?w=1200"];
  
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < allImages.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < allImages.length - 1) setCurrentIndex(prev => prev + 1);
  };

  const goToPrev = () => {
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
  };

  return (
    <div className="gallery-carousel" data-testid="image-gallery">
      <div 
        className="gallery-main"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {!isLoaded[currentIndex] && (
          <div className="gallery-loader">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        )}
        <img 
          src={allImages[currentIndex]} 
          alt={`${title} - Image ${currentIndex + 1}`}
          className={`gallery-image ${isLoaded[currentIndex] ? 'loaded' : ''}`}
          onLoad={() => setIsLoaded(prev => ({ ...prev, [currentIndex]: true }))}
          loading="lazy"
        />
        
        {allImages.length > 1 && (
          <>
            <button 
              className={`gallery-nav gallery-prev ${currentIndex === 0 ? 'disabled' : ''}`}
              onClick={goToPrev}
              disabled={currentIndex === 0}
              data-testid="gallery-prev"
            >
              <ChevronRight className="w-6 h-6 rotate-180" />
            </button>
            <button 
              className={`gallery-nav gallery-next ${currentIndex === allImages.length - 1 ? 'disabled' : ''}`}
              onClick={goToNext}
              disabled={currentIndex === allImages.length - 1}
              data-testid="gallery-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
        
        <div className="gallery-counter">
          {currentIndex + 1} / {allImages.length}
        </div>
      </div>
      
      {allImages.length > 1 && (
        <div className="gallery-dots">
          {allImages.map((_, index) => (
            <button
              key={index}
              className={`gallery-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              data-testid={`gallery-dot-${index}`}
            />
          ))}
        </div>
      )}
      
      {allImages.length > 1 && (
        <div className="gallery-thumbnails">
          {allImages.slice(0, 5).map((img, index) => (
            <button
              key={index}
              className={`gallery-thumb ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
            >
              <img src={img} alt={`Thumbnail ${index + 1}`} loading="lazy" />
            </button>
          ))}
          {allImages.length > 5 && (
            <div className="gallery-more">+{allImages.length - 5}</div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ PROPERTY DETAIL PAGE ============
const PropertyDetailPage = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [inquiryMessage, setInquiryMessage] = useState("");
  const [inquiryPhone, setInquiryPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bookingData, setBookingData] = useState({
    preferred_date: "",
    preferred_time: "10:00 AM",
    alternate_date: "",
    alternate_time: "",
    third_date: "",
    third_time: "",
    phone: "",
    message: ""
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  const timeSlots = [
    "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM"
  ];

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const { data } = await axios.get(`${API}/properties/${id}`);
        setProperty(data);
      } catch (e) {
        console.error("Error fetching property:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  const handleInquiry = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/app/login");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        property_id: id,
        message: inquiryMessage,
        phone: inquiryPhone
      });
      alert("Inquiry sent successfully! The landlord will be notified.");
      setShowInquiryForm(false);
      setInquiryMessage("");
      setInquiryPhone("");
    } catch (e) {
      alert("Failed to send inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/app/login");
      return;
    }
    if (!bookingData.preferred_date || !bookingData.phone) {
      alert("Please select a date and enter your phone number");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        property_id: id,
        ...bookingData
      });
      alert("Viewing request sent! The landlord will confirm your appointment.");
      setShowBookingForm(false);
      setBookingData({ preferred_date: "", preferred_time: "10:00 AM", alternate_date: "", alternate_time: "", third_date: "", third_time: "", phone: "", message: "" });
    } catch (e) {
      alert("Failed to submit booking request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!property) {
    return <div className="error-state">Property not found</div>;
  }

  return (
    <div className="property-detail-page" data-testid="property-detail-page">
      <button className="back-btn" onClick={() => navigate(-1)} data-testid="back-btn">
        <ArrowLeft className="w-5 h-5" /> Back
      </button>

      <div className="property-detail-grid">
        <ImageGallery images={property.images} title={property.title} />

        <div className="property-detail-info">
          <div className="property-header">
            <div>
              <span className="listing-badge">{property.listing_type === "rent" ? "For Rent" : "For Sale"}</span>
              <h1>{property.title}</h1>
              <p className="location"><MapPin className="w-5 h-5" /> {property.address}, {property.city}, {property.state} {property.zip_code}</p>
            </div>
            <div className="price-tag">${property.price?.toLocaleString()}{property.listing_type === "rent" ? "/mo" : ""}</div>
          </div>

          <div className="property-stats">
            <div className="stat"><Bed className="w-5 h-5" /><span>{property.bedrooms}</span><label>Bedrooms</label></div>
            <div className="stat"><Bath className="w-5 h-5" /><span>{property.bathrooms}</span><label>Bathrooms</label></div>
            <div className="stat"><Square className="w-5 h-5" /><span>{property.area_sqft?.toLocaleString()}</span><label>Sq Ft</label></div>
            <div className="stat"><Building2 className="w-5 h-5" /><span className="capitalize">{property.property_type}</span><label>Type</label></div>
          </div>

          {(property.lease_terms?.length > 0 || property.furnishing) && (
            <div className="property-lease-furnishing" data-testid="lease-furnishing-section">
              {property.lease_terms?.length > 0 && (
                <div className="lease-info">
                  <Clock className="w-5 h-5" />
                  <div>
                    <strong>Lease Term</strong>
                    <span>
                      {property.lease_terms.map(t => t === "long_term" ? "Long Term (1 Year+)" : "Short Term").join(" / ")}
                      {property.short_term_duration && <em className="duration-note"> — {property.short_term_duration}</em>}
                    </span>
                  </div>
                </div>
              )}
              {property.furnishing && (
                <div className="furnishing-info">
                  <Armchair className="w-5 h-5" />
                  <div>
                    <strong>Furnishing</strong>
                    <span>{property.furnishing === "furnished" ? "Furnished" : property.furnishing === "partially_furnished" ? "Partially Furnished" : "Unfurnished"}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {property.virtual_tour_url && (
            <div className="virtual-tour-section">
              <h3>3D Virtual Tour</h3>
              <a href={property.virtual_tour_url} target="_blank" rel="noopener noreferrer" className="virtual-tour-link">
                <Eye className="w-5 h-5" /> View Virtual Tour
              </a>
            </div>
          )}

          <div className="property-description">
            <h3>Description</h3>
            <p>{property.description}</p>
          </div>

          {property.amenities?.length > 0 && (
            <div className="property-amenities">
              <h3>Amenities</h3>
              <div className="amenities-list">
                {property.amenities.map((amenity, i) => (
                  <span key={i} className="amenity-tag"><Check className="w-4 h-4" /> {amenity}</span>
                ))}
              </div>
            </div>
          )}

          <div className="property-actions">
            <button className="btn-primary" onClick={() => setShowBookingForm(true)} data-testid="schedule-btn">
              <Calendar className="w-5 h-5" /> Schedule Viewing
            </button>
            <button className="btn-secondary" onClick={() => setShowInquiryForm(true)} data-testid="inquiry-btn">
              <MessageSquare className="w-5 h-5" /> Send Message
            </button>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingForm && (
        <div className="modal-overlay" onClick={() => setShowBookingForm(false)}>
          <div className="modal booking-modal" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
            <h2>Schedule a Viewing</h2>
            <p className="modal-subtitle">Pick up to 3 preferred times so the landlord can choose what works best</p>
            <form onSubmit={handleBooking}>
              <div className="slot-picker" data-testid="slot-picker">
                <div className="slot-row" data-testid="slot-1">
                  <span className="slot-label">1st choice</span>
                  <div className="slot-inputs">
                    <input
                      type="date"
                      value={bookingData.preferred_date}
                      onChange={(e) => setBookingData({ ...bookingData, preferred_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      data-testid="booking-date-1"
                    />
                    <select
                      value={bookingData.preferred_time}
                      onChange={(e) => setBookingData({ ...bookingData, preferred_time: e.target.value })}
                      data-testid="booking-time-1"
                    >
                      {timeSlots.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="slot-row" data-testid="slot-2">
                  <span className="slot-label">2nd choice</span>
                  <div className="slot-inputs">
                    <input
                      type="date"
                      value={bookingData.alternate_date}
                      onChange={(e) => setBookingData({ ...bookingData, alternate_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="booking-date-2"
                    />
                    <select
                      value={bookingData.alternate_time}
                      onChange={(e) => setBookingData({ ...bookingData, alternate_time: e.target.value })}
                      data-testid="booking-time-2"
                    >
                      <option value="">Select time</option>
                      {timeSlots.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="slot-row" data-testid="slot-3">
                  <span className="slot-label">3rd choice</span>
                  <div className="slot-inputs">
                    <input
                      type="date"
                      value={bookingData.third_date}
                      onChange={(e) => setBookingData({ ...bookingData, third_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="booking-date-3"
                    />
                    <select
                      value={bookingData.third_time}
                      onChange={(e) => setBookingData({ ...bookingData, third_time: e.target.value })}
                      data-testid="booking-time-3"
                    >
                      <option value="">Select time</option>
                      {timeSlots.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Your Phone Number *</label>
                <input
                  type="tel"
                  value={bookingData.phone}
                  onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                  placeholder="(416) 555-1234"
                  required
                  data-testid="booking-phone"
                />
                <p className="form-hint">The landlord will call you to confirm</p>
              </div>
              <div className="form-group">
                <label>Message (optional)</label>
                <textarea
                  value={bookingData.message}
                  onChange={(e) => setBookingData({ ...bookingData, message: e.target.value })}
                  placeholder="Any questions or notes for the landlord..."
                  rows={2}
                  data-testid="booking-message"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowBookingForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting} data-testid="submit-booking-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Request Viewing
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inquiry Modal */}
      {showInquiryForm && (
        <div className="modal-overlay" onClick={() => setShowInquiryForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="inquiry-modal">
            <h2>Contact About This Property</h2>
            <form onSubmit={handleInquiry}>
              <div className="form-group">
                <label>Message</label>
                <textarea
                  value={inquiryMessage}
                  onChange={(e) => setInquiryMessage(e.target.value)}
                  placeholder="I'm interested in this property..."
                  required
                  data-testid="inquiry-message"
                />
              </div>
              <div className="form-group">
                <label>Phone (optional)</label>
                <input
                  type="tel"
                  value={inquiryPhone}
                  onChange={(e) => setInquiryPhone(e.target.value)}
                  placeholder="Your phone number"
                  data-testid="inquiry-phone"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowInquiryForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting} data-testid="submit-inquiry-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Inquiry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ AI CHAT WIDGET ============
const AIChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your real estate assistant. How can I help you find your perfect property today?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = { role: "user", content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/ai/chat`, { message: input });
      setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`chat-widget ${isOpen ? "open" : ""}`} data-testid="ai-chat-widget">
      <button className="chat-toggle" onClick={() => setIsOpen(!isOpen)} data-testid="chat-toggle-btn">
        {isOpen ? <X /> : <Bot />}
      </button>

      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <Bot className="w-5 h-5" />
            <span>AI Assistant</span>
          </div>
          <div className="chat-messages" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chat-message ${msg.role}`}>
                {msg.content}
              </div>
            ))}
            {loading && (
              <div className="chat-message assistant">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            )}
          </div>
          <form className="chat-input-form" onSubmit={sendMessage}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything..."
              data-testid="chat-input"
            />
            <button type="submit" disabled={loading} data-testid="chat-send-btn">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

// ============ LOGIN PAGE ============
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/app/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) { setError("Email is required"); return; }
    if (!password) { setError("Password is required"); return; }

    setLoading(true);
    try {
      await login(email, password);
      navigate("/app/dashboard");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <div className="auth-card">
        <h1>Welcome Back</h1>
        <p>Sign in to your account</p>
        
        {error && <div className="error-message" data-testid="login-error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email <span className="required-star">*</span></label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="login-email" />
          </div>
          <div className="form-group">
            <label>Password <span className="required-star">*</span></label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Your password" data-testid="login-password" />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="login-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
        
        <p className="auth-switch">
          Don't have an account? <Link to="/app/register">Sign Up</Link>
        </p>
      </div>
    </div>
  );
};

// ============ REGISTER PAGE ============
const RegisterPage = () => {
  const [formData, setFormData] = useState({ 
    email: "", 
    password: "", 
    name: "", 
    role: "tenant", 
    phone: "",
    sms_notifications_enabled: false 
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/app/dashboard");
  }, [user, navigate]);

  // Auto-disable SMS if phone is cleared
  useEffect(() => {
    if (!formData.phone && formData.sms_notifications_enabled) {
      setFormData(prev => ({ ...prev, sms_notifications_enabled: false }));
    }
  }, [formData.phone, formData.sms_notifications_enabled]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim()) { setError("Name is required"); return; }
    if (!formData.email.trim()) { setError("Email is required"); return; }
    if (!/\S+@\S+\.\S+/.test(formData.email)) { setError("Please enter a valid email address"); return; }
    if (!formData.password) { setError("Password is required"); return; }
    if (formData.password.length < 6) { setError("Password must be at least 6 characters"); return; }

    setLoading(true);
    try {
      await register(
        formData.email, 
        formData.password, 
        formData.name, 
        formData.role, 
        formData.phone,
        formData.sms_notifications_enabled
      );
      navigate("/app/dashboard");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        const msg = detail.map(d => {
          const field = d.loc?.[d.loc.length - 1] || "field";
          return `${field.charAt(0).toUpperCase() + field.slice(1)}: ${d.msg}`;
        }).join(". ");
        setError(msg);
      } else {
        setError("Registration failed. Please check all required fields and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const showSmsOptions = formData.role === "landlord" || formData.role === "agent";

  return (
    <div className="auth-page" data-testid="register-page">
      <div className="auth-card">
        <h1>Create Account</h1>
        <p>Join our marketplace</p>
        
        {error && <div className="error-message" data-testid="register-error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name <span className="required-star">*</span></label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="Your full name" data-testid="register-name" />
          </div>
          <div className="form-group">
            <label>Email <span className="required-star">*</span></label>
            <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required placeholder="you@example.com" data-testid="register-email" />
          </div>
          <div className="form-group">
            <label>Password <span className="required-star">*</span></label>
            <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required placeholder="Min. 6 characters" data-testid="register-password" />
          </div>
          <div className="form-group">
            <label>I am a <span className="required-star">*</span></label>
            <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} data-testid="register-role">
              <option value="tenant">Tenant / Buyer</option>
              <option value="landlord">Landlord / Property Owner</option>
              <option value="agent">Agent / Broker</option>
            </select>
          </div>
          
          {showSmsOptions && (
            <div className="sms-options-section">
              <div className="form-group">
                <label className="label-with-badge">
                  Phone Number 
                  <span className="recommended-badge">Recommended</span>
                </label>
                <input 
                  type="tel" 
                  value={formData.phone} 
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                  placeholder="(416) 555-1234"
                  data-testid="register-phone" 
                />
                <p className="form-hint">Get instant alerts when tenants inquire about your properties</p>
              </div>
              
              <label className={`checkbox-field ${!formData.phone ? 'disabled' : ''}`}>
                <input 
                  type="checkbox" 
                  checked={formData.sms_notifications_enabled}
                  onChange={(e) => setFormData({ ...formData, sms_notifications_enabled: e.target.checked })}
                  disabled={!formData.phone}
                  data-testid="register-sms-checkbox"
                />
                <span className="checkbox-label">
                  <Phone className="w-4 h-4" />
                  Enable SMS notifications for new inquiries
                </span>
              </label>
              
              {formData.sms_notifications_enabled && (
                <p className="sms-enabled-note">
                  You'll receive instant text alerts when tenants contact you about your listings.
                </p>
              )}
            </div>
          )}
          
          <p className="sms-consent">
            By creating an account, you agree to our <Link to="/privacy-policy">Privacy Policy</Link> and <Link to="/terms-of-service">Terms of Service</Link>.
            {formData.sms_notifications_enabled && " Message and data rates may apply. Text STOP to cancel."}
          </p>
          
          <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="register-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
          </button>
        </form>
        
        <p className="auth-switch">
          Already have an account? <Link to="/app/login">Sign In</Link>
        </p>
      </div>
    </div>
  );
};

// ============ DASHBOARD PAGE ============
const DashboardPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [listings, setListings] = useState([]);
  const [inquiriesReceived, setInquiriesReceived] = useState([]);
  const [inquiriesSent, setInquiriesSent] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user.role === "landlord" || user.role === "agent") {
          const [listingsRes, inquiriesRes] = await Promise.all([
            axios.get(`${API}/properties/user/listings`),
            axios.get(`${API}/inquiries/received`)
          ]);
          setListings(listingsRes.data);
          setInquiriesReceived(inquiriesRes.data);
        }
        if (user.role === "tenant") {
          const { data } = await axios.get(`${API}/inquiries/sent`);
          setInquiriesSent(data);
        }
        if (user.role === "admin") {
          const { data } = await axios.get(`${API}/admin/stats`);
          setStats(data);
        }
      } catch (e) {
        console.error("Dashboard error:", e);
      }
    };
    fetchData();
  }, [user.role]);

  return (
    <div className="dashboard-page" data-testid="dashboard-page">
      <div className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="user-avatar">{user.name?.charAt(0).toUpperCase()}</div>
          <div>
            <h3>{user.name}</h3>
            <span className="user-role">{user.role}</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          <button className={activeTab === "overview" ? "active" : ""} onClick={() => setActiveTab("overview")} data-testid="tab-overview">
            <Home className="w-5 h-5" /> Overview
          </button>
          {(user.role === "landlord" || user.role === "agent") && (
            <>
              <button className={activeTab === "listings" ? "active" : ""} onClick={() => setActiveTab("listings")} data-testid="tab-listings">
                <Building2 className="w-5 h-5" /> My Listings
              </button>
              <button className={activeTab === "inquiries" ? "active" : ""} onClick={() => setActiveTab("inquiries")} data-testid="tab-inquiries">
                <MessageSquare className="w-5 h-5" /> Inquiries
              </button>
            </>
          )}
          {user.role === "tenant" && (
            <button className={activeTab === "my-inquiries" ? "active" : ""} onClick={() => setActiveTab("my-inquiries")} data-testid="tab-my-inquiries">
              <MessageSquare className="w-5 h-5" /> My Inquiries
            </button>
          )}
          {user.role === "admin" && (
            <button className={activeTab === "admin" ? "active" : ""} onClick={() => setActiveTab("admin")} data-testid="tab-admin">
              <Settings className="w-5 h-5" /> Admin
            </button>
          )}
          <Link to="/app/settings" className="sidebar-link" data-testid="tab-settings">
            <UserCog className="w-5 h-5" /> Settings
          </Link>
        </nav>
      </div>

      <div className="dashboard-content">
        {activeTab === "overview" && (
          <div className="dashboard-overview">
            <h2>Welcome back, {user.name}!</h2>
            {user.role === "admin" && stats && (
              <div className="stats-grid">
                <div className="stat-card"><Users className="w-6 h-6" /><span>{stats.total_users}</span><label>Users</label></div>
                <div className="stat-card"><Building2 className="w-6 h-6" /><span>{stats.total_properties}</span><label>Properties</label></div>
                <div className="stat-card"><Home className="w-6 h-6" /><span>{stats.active_properties}</span><label>Active Listings</label></div>
                <div className="stat-card"><MessageSquare className="w-6 h-6" /><span>{stats.total_inquiries}</span><label>Inquiries</label></div>
              </div>
            )}
            {(user.role === "landlord" || user.role === "agent") && (
              <div className="stats-grid">
                <div className="stat-card"><Building2 className="w-6 h-6" /><span>{listings.length}</span><label>My Listings</label></div>
                <div className="stat-card"><MessageSquare className="w-6 h-6" /><span>{inquiriesReceived.length}</span><label>Inquiries</label></div>
              </div>
            )}
            {user.role === "tenant" && (
              <div className="stats-grid">
                <div className="stat-card"><MessageSquare className="w-6 h-6" /><span>{inquiriesSent.length}</span><label>Inquiries Sent</label></div>
              </div>
            )}
          </div>
        )}

        {activeTab === "listings" && (
          <div className="dashboard-listings">
            <div className="section-header">
              <h2>My Listings</h2>
              <Link to="/app/dashboard/new-listing" className="btn-primary" data-testid="new-listing-btn">
                <Plus className="w-5 h-5" /> Add Listing
              </Link>
            </div>
            <div className="listings-table">
              {listings.length === 0 ? (
                <p className="empty-message">No listings yet</p>
              ) : (
                listings.map((listing) => (
                  <div key={listing.id} className="listing-row" data-testid={`listing-row-${listing.id}`}>
                    <img src={listing.images?.[0] || "https://via.placeholder.com/100"} alt={listing.title} />
                    <div className="listing-info">
                      <h3>{listing.title}</h3>
                      <p>{listing.city}, {listing.state}</p>
                    </div>
                    <span className="listing-price">${listing.price?.toLocaleString()}/mo</span>
                    {listing.featured ? (
                      <span className="listing-tier featured" data-testid={`tier-${listing.id}`}>Featured</span>
                    ) : (
                      <button
                        className="boost-btn"
                        onClick={async () => {
                          try {
                            const { data } = await axios.post(`${API}/payments/boost`, {
                              property_id: listing.id,
                              origin_url: window.location.origin
                            });
                            if (data.url) window.location.href = data.url;
                          } catch (e) {
                            console.error("Boost error:", e);
                          }
                        }}
                        data-testid={`boost-btn-${listing.id}`}
                      >
                        <Star className="w-4 h-4" /> Boost $29
                      </button>
                    )}
                    <span className={`listing-status ${listing.status}`}>{listing.status}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === "inquiries" && (
          <div className="dashboard-inquiries">
            <h2>Inquiries Received</h2>
            {inquiriesReceived.length === 0 ? (
              <p className="empty-message">No inquiries yet</p>
            ) : (
              inquiriesReceived.map((inquiry) => (
                <div key={inquiry.id} className="inquiry-card">
                  <div className="inquiry-header">
                    <h3>{inquiry.property_title}</h3>
                    <span className={`inquiry-status ${inquiry.status}`}>{inquiry.status}</span>
                  </div>
                  <p className="inquiry-message">{inquiry.message}</p>
                  <div className="inquiry-contact">
                    <span><Mail className="w-4 h-4" /> {inquiry.sender_email}</span>
                    {inquiry.phone && <span><Phone className="w-4 h-4" /> {inquiry.phone}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "my-inquiries" && (
          <div className="dashboard-inquiries">
            <h2>My Inquiries</h2>
            {inquiriesSent.length === 0 ? (
              <p className="empty-message">No inquiries sent yet</p>
            ) : (
              inquiriesSent.map((inquiry) => (
                <div key={inquiry.id} className="inquiry-card">
                  <div className="inquiry-header">
                    <h3>{inquiry.property_title}</h3>
                    <span className={`inquiry-status ${inquiry.status}`}>{inquiry.status}</span>
                  </div>
                  <p className="inquiry-message">{inquiry.message}</p>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === "admin" && user.role === "admin" && (
          <div className="admin-panel">
            <h2>Admin Panel</h2>
            <p>Manage users, properties, and platform settings.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ NEW LISTING PAGE ============
const NewListingPage = () => {
  const [formData, setFormData] = useState({
    title: "", description: "", property_type: "apartment", listing_type: "rent",
    price: "", bedrooms: "", bathrooms: "", area_sqft: "",
    address: "", city: "", state: "", zip_code: "",
    amenities: [], images: [], virtual_tour_url: "", floor_plan_url: "",
    lease_terms: [], short_term_duration: "", furnishing: ""
  });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingFloorPlan, setGeneratingFloorPlan] = useState(false);
  const [exportingFloorPlan, setExportingFloorPlan] = useState(false);
  const [floorPlanData, setFloorPlanData] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const amenityOptions = ["Gym", "Pool", "Parking", "Laundry", "Pet Friendly", "Balcony", "Furnished", "Dishwasher", "Air Conditioning", "Heating", "In-Unit Laundry", "Storage", "Concierge", "Rooftop"];

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append("files", file));
      
      const { data } = await axios.post(`${API}/uploads/images`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const newUrls = data.urls.map(url => `${BACKEND_URL}${url}`);
      setUploadedImages(prev => [...prev, ...newUrls]);
    } catch (e) {
      alert("Failed to upload images. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const generateDescription = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/ai/generate-description`, {
        property_type: formData.property_type,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        area_sqft: formData.area_sqft,
        city: formData.city,
        state: formData.state,
        amenities: formData.amenities
      });
      setFormData(prev => ({ ...prev, description: data.description }));
    } catch (e) {
      alert("Failed to generate description");
    } finally {
      setGenerating(false);
    }
  };

  const generateFloorPlan = async () => {
    if (!formData.bedrooms || !formData.bathrooms || !formData.area_sqft) {
      alert("Please fill in bedrooms, bathrooms, and area first");
      return;
    }
    if (uploadedImages.length === 0) {
      alert("Please upload at least one property image to generate a floor plan");
      return;
    }
    
    setGeneratingFloorPlan(true);
    try {
      const { data } = await axios.post(`${API}/ai/generate-floor-plan`, {
        images: uploadedImages,
        property_type: formData.property_type,
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseFloat(formData.bathrooms),
        area_sqft: parseFloat(formData.area_sqft)
      });
      setFloorPlanData(data);
    } catch (e) {
      alert("Failed to generate floor plan");
    } finally {
      setGeneratingFloorPlan(false);
    }
  };

  // Export floor plan as JPEG or PDF
  const exportFloorPlan = async (format) => {
    if (!floorPlanData?.floor_plan) {
      alert("Please generate a floor plan first");
      return;
    }
    
    setExportingFloorPlan(true);
    try {
      const { data } = await axios.post(`${API}/uploads/floorplan/export`, {
        floor_plan: floorPlanData.floor_plan,
        format: format,
        property_title: formData.title || "Property Floor Plan"
      });
      
      // Open download link
      window.open(`${BACKEND_URL}${data.url}`, "_blank");
    } catch (e) {
      alert("Failed to export floor plan");
    } finally {
      setExportingFloorPlan(false);
    }
  };

  const [createdListingId, setCreatedListingId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseFloat(formData.bathrooms),
        area_sqft: parseFloat(formData.area_sqft),
        images: uploadedImages
      };
      const { data } = await axios.post(`${API}/properties`, payload);
      setCreatedListingId(data.id);
    } catch (e) {
      alert("Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  const handleBoost = async () => {
    try {
      const { data } = await axios.post(`${API}/payments/boost`, {
        property_id: createdListingId,
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error("Boost error:", e);
    }
  };

  if (createdListingId) {
    return (
      <div className="boost-cta-page" data-testid="boost-cta-page">
        <div className="boost-cta-card">
          <div className="boost-cta-icon">
            <Star className="w-12 h-12" />
          </div>
          <h2>Listing Published!</h2>
          <p className="boost-cta-subtitle">Your listing is now live. Want more visibility?</p>
          
          <div className="boost-offer-card" data-testid="boost-offer-card">
            <div className="boost-offer-header">
              <span className="boost-price-tag">$29 <small>/month</small></span>
              <span className="boost-multiplier">5x more views</span>
            </div>
            <ul className="boost-benefits">
              <li><Check className="w-4 h-4" /> Pinned to top of search results</li>
              <li><Check className="w-4 h-4" /> "Featured" badge on your listing</li>
              <li><Check className="w-4 h-4" /> Highlighted on the map</li>
              <li><Check className="w-4 h-4" /> 30 days of premium placement</li>
            </ul>
            <button className="btn-boost" onClick={handleBoost} data-testid="boost-now-btn">
              <Star className="w-5 h-5" /> Feature This Listing for $29
            </button>
          </div>
          
          <button className="btn-text" onClick={() => navigate("/app/dashboard")} data-testid="skip-boost-btn">
            No thanks, go to dashboard
          </button>
        </div>
      </div>
    );
  }

  // Floor plan renderer with export buttons
  const FloorPlanViewer = ({ data }) => {
    if (!data?.floor_plan?.rooms) return null;
    const { rooms, total_width, total_height } = data.floor_plan;
    const scale = 15;
    
    return (
      <div className="floor-plan-viewer">
        <div className="floor-plan-header">
          <h4>Generated Floor Plan</h4>
          <div className="export-buttons">
            <button 
              type="button"
              className="btn-export" 
              onClick={() => exportFloorPlan("jpeg")}
              disabled={exportingFloorPlan}
              data-testid="export-jpeg-btn"
            >
              {exportingFloorPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileImage className="w-4 h-4" />}
              Download JPEG
            </button>
            <button 
              type="button"
              className="btn-export btn-export-pdf" 
              onClick={() => exportFloorPlan("pdf")}
              disabled={exportingFloorPlan}
              data-testid="export-pdf-btn"
            >
              {exportingFloorPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Download PDF
            </button>
          </div>
        </div>
        <div className="floor-plan-preview">
          <svg 
            viewBox={`0 0 ${(total_width || 40) * scale} ${(total_height || 30) * scale + 60}`}
            className="floor-plan-svg"
          >
            {/* GTA Rental Hub watermark at top */}
            <text
              x={(total_width || 40) * scale / 2}
              y="20"
              textAnchor="middle"
              fontSize="14"
              fill="#0055FF"
              fontWeight="bold"
            >
              GTA Rental Hub
            </text>
            
            {rooms.map((room, i) => (
              <g key={i}>
                <rect
                  x={(room.x || 0) * scale}
                  y={(room.y || 0) * scale + 35}
                  width={(room.width || 10) * scale}
                  height={(room.height || 8) * scale}
                  fill="#f0f4f8"
                  stroke="#0055FF"
                  strokeWidth="2"
                />
                <text
                  x={(room.x || 0) * scale + (room.width || 10) * scale / 2}
                  y={(room.y || 0) * scale + (room.height || 8) * scale / 2 + 35}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="12"
                  fill="#0A2540"
                  fontWeight="600"
                >
                  {room.name}
                </text>
                <text
                  x={(room.x || 0) * scale + (room.width || 10) * scale / 2}
                  y={(room.y || 0) * scale + (room.height || 8) * scale / 2 + 49}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="9"
                  fill="#666"
                >
                  {room.width}' x {room.height}'
                </text>
              </g>
            ))}
          </svg>
        </div>
        {data.floor_plan.notes && <p className="floor-plan-notes">{data.floor_plan.notes}</p>}
      </div>
    );
  };

  return (
    <div className="new-listing-page" data-testid="new-listing-page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" /> Back
        </button>
        <h1>Create New Listing</h1>
      </div>

      <form className="listing-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Basic Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Title</label>
              <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required data-testid="listing-title" />
            </div>
            <div className="form-group">
              <label>Property Type</label>
              <select value={formData.property_type} onChange={(e) => setFormData({ ...formData, property_type: e.target.value })} data-testid="listing-property-type">
                <option value="apartment">Apartment</option>
                <option value="house">House</option>
                <option value="condo">Condo</option>
                <option value="basement">Basement</option>
                <option value="townhouse">Townhouse</option>
              </select>
            </div>
            <div className="form-group">
              <label>Listing Type</label>
              <select value={formData.listing_type} onChange={(e) => setFormData({ ...formData, listing_type: e.target.value })} data-testid="listing-listing-type">
                <option value="rent">For Rent</option>
                <option value="sale">For Sale</option>
              </select>
            </div>
            <div className="form-group">
              <label>Price {formData.listing_type === "rent" ? "($/month)" : "($)"}</label>
              <input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required data-testid="listing-price" />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Property Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Bedrooms</label>
              <input type="number" value={formData.bedrooms} onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })} required data-testid="listing-bedrooms" />
            </div>
            <div className="form-group">
              <label>Bathrooms</label>
              <input type="number" step="0.5" value={formData.bathrooms} onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })} required data-testid="listing-bathrooms" />
            </div>
            <div className="form-group">
              <label>Area (sqft)</label>
              <input type="number" value={formData.area_sqft} onChange={(e) => setFormData({ ...formData, area_sqft: e.target.value })} required data-testid="listing-area" />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Lease & Furnishing</h3>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Lease Term (select all that apply)</label>
              <div className="checkbox-group" data-testid="lease-term-checkboxes">
                <label className={`checkbox-option ${formData.lease_terms.includes("long_term") ? "selected" : ""}`}>
                  <input
                    type="checkbox"
                    checked={formData.lease_terms.includes("long_term")}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      lease_terms: prev.lease_terms.includes("long_term")
                        ? prev.lease_terms.filter(t => t !== "long_term")
                        : [...prev.lease_terms, "long_term"]
                    }))}
                    data-testid="lease-long-term"
                  />
                  Long Term (1 Year+)
                </label>
                <label className={`checkbox-option ${formData.lease_terms.includes("short_term") ? "selected" : ""}`}>
                  <input
                    type="checkbox"
                    checked={formData.lease_terms.includes("short_term")}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      lease_terms: prev.lease_terms.includes("short_term")
                        ? prev.lease_terms.filter(t => t !== "short_term")
                        : [...prev.lease_terms, "short_term"]
                    }))}
                    data-testid="lease-short-term"
                  />
                  Short Term (Less than 1 Year)
                </label>
              </div>
              {formData.lease_terms.includes("short_term") && (
                <div className="short-term-duration" data-testid="short-term-duration-field">
                  <label>Specify min/max duration</label>
                  <input
                    type="text"
                    placeholder="e.g., 1-6 months"
                    value={formData.short_term_duration}
                    onChange={(e) => setFormData({ ...formData, short_term_duration: e.target.value })}
                    data-testid="short-term-duration-input"
                  />
                </div>
              )}
            </div>
            <div className="form-group full-width">
              <label>Furnishing</label>
              <div className="radio-group" data-testid="furnishing-radios">
                <label className={`radio-option ${formData.furnishing === "furnished" ? "selected" : ""}`}>
                  <input type="radio" name="furnishing" value="furnished" checked={formData.furnishing === "furnished"} onChange={(e) => setFormData({ ...formData, furnishing: e.target.value })} data-testid="furnishing-furnished" />
                  Furnished
                </label>
                <label className={`radio-option ${formData.furnishing === "unfurnished" ? "selected" : ""}`}>
                  <input type="radio" name="furnishing" value="unfurnished" checked={formData.furnishing === "unfurnished"} onChange={(e) => setFormData({ ...formData, furnishing: e.target.value })} data-testid="furnishing-unfurnished" />
                  Unfurnished
                </label>
                <label className={`radio-option ${formData.furnishing === "partially_furnished" ? "selected" : ""}`}>
                  <input type="radio" name="furnishing" value="partially_furnished" checked={formData.furnishing === "partially_furnished"} onChange={(e) => setFormData({ ...formData, furnishing: e.target.value })} data-testid="furnishing-partially" />
                  Partially Furnished
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Location</h3>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Address</label>
              <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required data-testid="listing-address" />
            </div>
            <div className="form-group">
              <label>City</label>
              <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required data-testid="listing-city" />
            </div>
            <div className="form-group">
              <label>State/Province</label>
              <input type="text" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} required data-testid="listing-state" />
            </div>
            <div className="form-group">
              <label>ZIP/Postal Code</label>
              <input type="text" value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} required data-testid="listing-zip" />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Description</h3>
          <div className="form-group">
            <div className="description-header">
              <label>Property Description</label>
              <button type="button" className="btn-secondary" onClick={generateDescription} disabled={generating} data-testid="generate-description-btn">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
                AI Generate
              </button>
            </div>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={5} required data-testid="listing-description" />
          </div>
        </div>

        <div className="form-section">
          <h3>Media & Virtual Tours</h3>
          <div className="form-group full-width">
            <label>Property Images</label>
            <div className="image-upload-area">
              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageUpload}
                style={{ display: "none" }}
                data-testid="image-file-input"
              />
              <button 
                type="button" 
                className="upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-images-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-5 h-5" /> Upload Images</>
                )}
              </button>
              <p className="upload-hint">JPEG, PNG, WebP or GIF • Max 10MB per file</p>
            </div>
            
            {uploadedImages.length > 0 && (
              <div className="uploaded-images-grid" data-testid="uploaded-images">
                {uploadedImages.map((url, index) => (
                  <div key={index} className="uploaded-image-item">
                    <img src={url} alt={`Property ${index + 1}`} />
                    <button 
                      type="button"
                      className="remove-image-btn"
                      onClick={() => removeImage(index)}
                      data-testid={`remove-image-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {index === 0 && <span className="primary-badge">Primary</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="form-group full-width" style={{ marginTop: "1.5rem" }}>
            <label>3D Virtual Tour URL <span className="label-hint">(Matterport, iGuide, or similar)</span></label>
            <input 
              type="url" 
              value={formData.virtual_tour_url} 
              onChange={(e) => setFormData({ ...formData, virtual_tour_url: e.target.value })} 
              placeholder="https://my.matterport.com/show/?m=..."
              data-testid="listing-virtual-tour"
            />
          </div>
        </div>

        <div className="form-section floor-plan-section">
          <div className="section-header-inline">
            <h3>AI Floor Plan Generator</h3>
            <span className="beta-badge">BETA</span>
          </div>
          <p className="section-description">Upload property photos and our AI will generate an estimated floor plan layout. Great for GTA listings!</p>
          <div className="floor-plan-generator">
            <button 
              type="button" 
              className="btn-primary" 
              onClick={generateFloorPlan} 
              disabled={generatingFloorPlan}
              data-testid="generate-floor-plan-btn"
            >
              {generatingFloorPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
              Generate Floor Plan from Photos
            </button>
            {floorPlanData && (
              <div className="floor-plan-result">
                {floorPlanData.floor_plan ? (
                  <FloorPlanViewer data={floorPlanData} />
                ) : (
                  <div className="floor-plan-text">
                    <h4>Layout Analysis</h4>
                    <p>{floorPlanData.raw_analysis}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Amenities</h3>
          <div className="amenities-grid">
            {amenityOptions.map((amenity) => (
              <label key={amenity} className={`amenity-checkbox ${formData.amenities.includes(amenity) ? "selected" : ""}`}>
                <input type="checkbox" checked={formData.amenities.includes(amenity)} onChange={() => handleAmenityToggle(amenity)} />
                {amenity}
              </label>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={loading} data-testid="submit-listing-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Listing
          </button>
        </div>
      </form>
    </div>
  );
};

// ============ APP LAYOUT ============
// ============ FOOTER ============
const Footer = () => {
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="footer-container">
        <div className="footer-brand">
          <div className="footer-logo">
            <div className="logo-icon">M</div>
            <span>GTA Rental Hub</span>
          </div>
          <p>Your premier Greater Toronto Area real estate marketplace.</p>
        </div>
        <div className="footer-links-section">
          <h4>Quick Links</h4>
          <Link to="/app">Browse Properties</Link>
          <Link to="/app/register">List Your Property</Link>
          <Link to="/app/login">Sign In</Link>
        </div>
        <div className="footer-links-section">
          <h4>Legal</h4>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-service">Terms of Service</Link>
        </div>
        <div className="footer-links-section">
          <h4>Contact</h4>
          <p>info@gtarentalhub.ca</p>
          <p>Toronto, Ontario, Canada</p>
        </div>
      </div>
      <div className="footer-bottom">
        <p>© 2026 GTA Rental Hub. All rights reserved.</p>
        <div className="footer-legal-links">
          <Link to="/privacy-policy">Privacy</Link>
          <span>•</span>
          <Link to="/terms-of-service">Terms</Link>
        </div>
      </div>
    </footer>
  );
};

// ============ PWA INSTALL BANNER ============
const InstallBanner = () => {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [qualified, setQualified] = useState(false);
  const timerRef = useRef(null);
  const location = useLocation();
  const searchCountRef = useRef(0);

  // Detect iOS Safari
  useEffect(() => {
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const standalone = window.navigator.standalone === true;
    setIsIOS(ios && !standalone);
  }, []);

  // Listen for native install prompt (Android/Chrome)
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // 30-second timer
  useEffect(() => {
    timerRef.current = setTimeout(() => setQualified(true), 30000);
    return () => clearTimeout(timerRef.current);
  }, []);

  // Track searches — qualifying after 1 navigation to /app/properties with a query
  useEffect(() => {
    if (location.pathname === '/app/properties' && location.search.includes('city=')) {
      searchCountRef.current += 1;
      if (searchCountRef.current >= 1) setQualified(true);
    }
  }, [location]);

  // Show banner once qualified + not already dismissed
  useEffect(() => {
    if (!qualified) return;
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;
    // Only show if not already installed as standalone
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (deferredPrompt || isIOS) setShow(true);
  }, [qualified, deferredPrompt, isIOS]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
    }
    setShow(false);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  };

  if (!show) return null;

  return (
    <div className="pwa-install-banner" data-testid="pwa-install-banner">
      <div className="pwa-install-inner">
        <div className="pwa-install-icon">
          <Smartphone size={22} />
        </div>
        <div className="pwa-install-text">
          <strong>Get the GTA Rental Hub App</strong>
          {isIOS ? (
            <span>Tap <Share2 size={14} style={{ verticalAlign: 'middle', margin: '0 2px' }} /> then <strong>"Add to Home Screen"</strong></span>
          ) : (
            <span>Install for quick access — no app store needed</span>
          )}
        </div>
        <div className="pwa-install-actions">
          {!isIOS && (
            <button className="pwa-install-btn" onClick={handleInstall} data-testid="pwa-install-btn">
              Install
            </button>
          )}
          <button className="pwa-dismiss-btn" onClick={handleDismiss} data-testid="pwa-dismiss-btn" aria-label="Dismiss">
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

const AppLayout = ({ children }) => {
  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={MAPS_LIBRARIES}>
      <div className="app-layout">
        <Navbar />
        <main className="main-content">{children}</main>
        <InstallBanner />
        <Footer />
      </div>
    </LoadScript>
  );
};

// ============ PRIVACY POLICY PAGE ============
const PrivacyPolicyPage = () => {
  return (
    <div className="legal-page" data-testid="privacy-policy-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: January 2026</p>
        
        <section className="legal-section">
          <h2>SMS Privacy Policy</h2>
          <p>
            GTA Rental Hub is committed to protecting your privacy. We collect your phone number solely for the purpose of account verification and security via SMS. Your mobile information will not be shared with third parties or affiliates for marketing or promotional purposes. All other categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
          </p>
        </section>
        
        <section className="legal-section">
          <h2>Information We Collect</h2>
          <p>We collect information you provide directly to us, including:</p>
          <ul>
            <li>Name and email address when you create an account</li>
            <li>Phone number for SMS notifications and verification</li>
            <li>Property listing information if you are a landlord or agent</li>
            <li>Communication data when you contact us or use our messaging features</li>
          </ul>
        </section>
        
        <section className="legal-section">
          <h2>How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve our services</li>
            <li>Send you notifications about property inquiries and bookings</li>
            <li>Verify your identity through SMS verification codes</li>
            <li>Communicate with you about your account and our services</li>
          </ul>
        </section>
        
        <section className="legal-section">
          <h2>Contact Us</h2>
          <p>If you have any questions about this Privacy Policy, please contact us at privacy@gtarentalhub.ca</p>
        </section>
      </div>
    </div>
  );
};

// ============ TERMS OF SERVICE PAGE ============
const TermsOfServicePage = () => {
  return (
    <div className="legal-page" data-testid="terms-of-service-page">
      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: January 2026</p>
        
        <section className="legal-section">
          <h2>SMS Terms and Conditions</h2>
          <p>
            By providing your phone number, you agree to receive one-time passcodes and account notifications from GTA Rental Hub. Message frequency varies. Message and data rates may apply. You can cancel the SMS service at any time by texting 'STOP'. For help, text 'HELP'. Carriers are not liable for delayed or undelivered messages.
          </p>
        </section>
        
        <section className="legal-section">
          <h2>Acceptance of Terms</h2>
          <p>
            By accessing or using the GTA Rental Hub platform, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using this site.
          </p>
        </section>
        
        <section className="legal-section">
          <h2>Use of Service</h2>
          <p>You agree to use GTA Rental Hub only for lawful purposes and in accordance with these Terms. You agree not to:</p>
          <ul>
            <li>Use the service in any way that violates any applicable law or regulation</li>
            <li>Post false, misleading, or fraudulent property listings</li>
            <li>Impersonate any person or entity</li>
            <li>Interfere with or disrupt the service or servers</li>
          </ul>
        </section>
        
        <section className="legal-section">
          <h2>User Accounts</h2>
          <p>
            You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.
          </p>
        </section>
        
        <section className="legal-section">
          <h2>Property Listings</h2>
          <p>
            Landlords and agents are responsible for the accuracy of their property listings. GTA Rental Hub does not guarantee the accuracy of any listing information and is not responsible for any disputes between users.
          </p>
        </section>
        
        <section className="legal-section">
          <h2>Limitation of Liability</h2>
          <p>
            GTA Rental Hub shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the service.
          </p>
        </section>
        
        <section className="legal-section">
          <h2>Contact Us</h2>
          <p>If you have any questions about these Terms, please contact us at legal@gtarentalhub.ca</p>
        </section>
      </div>
    </div>
  );
};

// ============ PROFILE SETTINGS PAGE ============
const ProfileSettingsPage = () => {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [smsEnabled, setSmsEnabled] = useState(user?.sms_notifications_enabled || false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // Phone verification state
  const [phoneInput, setPhoneInput] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [phoneMsg, setPhoneMsg] = useState("");
  const [removingPhone, setRemovingPhone] = useState(false);

  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      await axios.put(`${API}/auth/profile`, { name, sms_notifications_enabled: smsEnabled });
      await checkAuth();
      setSaveMsg("Profile updated successfully");
    } catch (e) {
      setSaveMsg(e.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(""), 3000);
    }
  };

  const handleSendCode = async () => {
    if (!phoneInput.trim()) return;
    setSendingCode(true);
    setPhoneMsg("");
    try {
      await axios.post(`${API}/auth/phone/send-code`, { phone: phoneInput });
      setCodeSent(true);
      setPhoneMsg("Verification code sent! Check your phone.");
    } catch (e) {
      setPhoneMsg(e.response?.data?.detail || "Failed to send code");
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) return;
    setVerifying(true);
    setPhoneMsg("");
    try {
      await axios.post(`${API}/auth/phone/verify-code`, { phone: phoneInput, code: verificationCode });
      await checkAuth();
      setCodeSent(false);
      setVerificationCode("");
      setPhoneInput("");
      setPhoneMsg("Phone number verified successfully!");
    } catch (e) {
      setPhoneMsg(e.response?.data?.detail || "Invalid code");
    } finally {
      setVerifying(false);
    }
  };

  const handleRemovePhone = async () => {
    setRemovingPhone(true);
    setPhoneMsg("");
    try {
      await axios.delete(`${API}/auth/phone`);
      await checkAuth();
      setSmsEnabled(false);
      setPhoneMsg("Phone number removed");
    } catch (e) {
      setPhoneMsg(e.response?.data?.detail || "Failed to remove phone");
    } finally {
      setRemovingPhone(false);
      setTimeout(() => setPhoneMsg(""), 3000);
    }
  };

  return (
    <div className="settings-page" data-testid="settings-page">
      <div className="settings-container">
        <div className="settings-header">
          <Link to="/app/dashboard" className="back-link" data-testid="settings-back">
            <ArrowLeft className="w-5 h-5" /> Back to Dashboard
          </Link>
          <h1>Profile Settings</h1>
          <p>Manage your account, phone number, and notification preferences</p>
        </div>

        {/* Personal Info Section */}
        <div className="settings-card" data-testid="settings-personal">
          <div className="settings-card-header">
            <UserCog className="w-5 h-5" />
            <h2>Personal Information</h2>
          </div>
          <div className="settings-card-body">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="settings-name"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={user?.email || ""} disabled className="input-disabled" data-testid="settings-email" />
              <p className="form-hint">Email cannot be changed</p>
            </div>
            <div className="form-group">
              <label>Role</label>
              <input type="text" value={user?.role || ""} disabled className="input-disabled" />
            </div>
          </div>
        </div>

        {/* Phone Verification Section */}
        <div className="settings-card" data-testid="settings-phone-section">
          <div className="settings-card-header">
            <Shield className="w-5 h-5" />
            <h2>Phone Number</h2>
          </div>
          <div className="settings-card-body">
            {user?.phone ? (
              <div className="phone-current">
                <div className="phone-display">
                  <Phone className="w-5 h-5" />
                  <span data-testid="settings-current-phone">{user.phone}</span>
                  {user.phone_verified && <span className="verified-badge" data-testid="phone-verified-badge">Verified</span>}
                </div>
                <div className="phone-actions">
                  <button
                    className="btn-outline-sm"
                    onClick={() => { setPhoneInput(""); setCodeSent(false); setVerificationCode(""); }}
                    data-testid="change-phone-btn"
                  >
                    Change Number
                  </button>
                  <button
                    className="btn-danger-sm"
                    onClick={handleRemovePhone}
                    disabled={removingPhone}
                    data-testid="remove-phone-btn"
                  >
                    {removingPhone ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remove"}
                  </button>
                </div>
              </div>
            ) : (
              <p className="form-hint" style={{margin: 0}}>No phone number on file. Add one to receive SMS alerts.</p>
            )}

            {(!user?.phone || phoneInput !== undefined) && !user?.phone && (
              <div className="phone-verify-flow">
                {!codeSent ? (
                  <div className="phone-input-group">
                    <div className="form-group">
                      <label>Phone Number</label>
                      <input
                        type="tel"
                        value={phoneInput}
                        onChange={(e) => setPhoneInput(e.target.value)}
                        placeholder="(416) 555-1234"
                        data-testid="settings-phone-input"
                      />
                    </div>
                    <button
                      className="btn-primary"
                      onClick={handleSendCode}
                      disabled={sendingCode || !phoneInput.trim()}
                      data-testid="send-code-btn"
                    >
                      {sendingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Verification Code"}
                    </button>
                  </div>
                ) : (
                  <div className="code-input-group">
                    <p className="code-sent-msg">We sent a 6-digit code to <strong>{phoneInput}</strong></p>
                    <div className="form-group">
                      <label>Verification Code</label>
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        className="code-input"
                        data-testid="settings-verify-code"
                      />
                    </div>
                    <div className="code-actions">
                      <button
                        className="btn-primary"
                        onClick={handleVerifyCode}
                        disabled={verifying || verificationCode.length !== 6}
                        data-testid="verify-code-btn"
                      >
                        {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
                      </button>
                      <button className="btn-text" onClick={() => { setCodeSent(false); setVerificationCode(""); }} data-testid="cancel-verify-btn">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {phoneMsg && (
              <p className={`settings-msg ${phoneMsg.includes("success") || phoneMsg.includes("sent") || phoneMsg.includes("verified") ? "success" : "error"}`} data-testid="phone-msg">
                {phoneMsg}
              </p>
            )}
          </div>
        </div>

        {/* SMS Notifications Section */}
        {(user?.role === "landlord" || user?.role === "agent") && (
          <div className="settings-card" data-testid="settings-sms-section">
            <div className="settings-card-header">
              <Bell className="w-5 h-5" />
              <h2>SMS Notifications</h2>
            </div>
            <div className="settings-card-body">
              <label className={`checkbox-field settings-toggle ${!user?.phone ? "disabled" : ""}`}>
                <input
                  type="checkbox"
                  checked={smsEnabled}
                  onChange={(e) => setSmsEnabled(e.target.checked)}
                  disabled={!user?.phone}
                  data-testid="settings-sms-toggle"
                />
                <span className="checkbox-label">
                  <Phone className="w-4 h-4" />
                  Enable instant SMS alerts for new inquiries
                </span>
              </label>
              {!user?.phone && (
                <p className="form-hint">Add a verified phone number above to enable SMS notifications</p>
              )}
              {smsEnabled && user?.phone && (
                <div className="sms-enabled-note">
                  You'll receive text alerts at {user.phone} when tenants inquire about your properties.
                </div>
              )}
              <p className="sms-consent" style={{marginTop: "1rem"}}>
                SMS notifications are subject to our <Link to="/privacy-policy">Privacy Policy</Link> and <Link to="/terms-of-service">Terms of Service</Link>. 
                Message and data rates may apply. Text STOP to cancel. CASL compliant.
              </p>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="settings-save-row">
          <button
            className="btn-primary btn-lg"
            onClick={handleSaveProfile}
            disabled={saving}
            data-testid="settings-save-btn"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Changes"}
          </button>
          {saveMsg && (
            <span className={`settings-msg ${saveMsg.includes("success") ? "success" : "error"}`} data-testid="settings-save-msg">
              {saveMsg}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ MAIN APP ============
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Coming Soon - Root */}
            <Route path="/" element={<Navigate to="/app" replace />} />
            
            {/* Legal Pages - Required for Twilio */}
            <Route path="/privacy-policy" element={<AppLayout><PrivacyPolicyPage /></AppLayout>} />
            <Route path="/terms-of-service" element={<AppLayout><TermsOfServicePage /></AppLayout>} />
            
            {/* Marketplace App */}
            <Route path="/app" element={<AppLayout><HomePage /></AppLayout>} />
            <Route path="/app/properties" element={<AppLayout><PropertiesPage /></AppLayout>} />
            <Route path="/app/properties/:id" element={<AppLayout><PropertyDetailPage /></AppLayout>} />
            <Route path="/app/login" element={<AppLayout><LoginPage /></AppLayout>} />
            <Route path="/app/register" element={<AppLayout><RegisterPage /></AppLayout>} />
            <Route path="/app/dashboard" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/dashboard/new-listing" element={<ProtectedRoute roles={["landlord", "agent", "admin"]}><AppLayout><NewListingPage /></AppLayout></ProtectedRoute>} />
            <Route path="/app/settings" element={<ProtectedRoute><AppLayout><ProfileSettingsPage /></AppLayout></ProtectedRoute>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
